import type { MutableRefObject } from "react"
import { useRef, useEffect } from "react"

import { atomFamily, selectorFamily, useSetRecoilState } from "recoil"

import { findScrollPositionState } from "../containers/LegacyDesigner/state/scroll"
import type { Point2d } from "../utils/geometry"

const ORIGIN = { x: 0, y: 0 }

export const findMousePositionState = atomFamily<Point2d, string>({
  key: `mouse`,
  default: ORIGIN,
})

export const translatePoint: (p: Point2d, displacement: Point2d) => Point2d = (
  { x, y },
  displacement,
) => ({ x: x + displacement.x, y: y + displacement.y })

export const translateMousePosition = (
  mousePosition: Point2d,
  ...offsets: Point2d[]
): Point2d =>
  offsets.reduce((tally, offset) => translatePoint(tally, offset), mousePosition)

export const findProjectedMouseState = selectorFamily<Point2d, string[]>({
  key: `offsetMouse`,
  get:
    ([mousePosKey, ...scrollPosKeys]) =>
    ({ get }) => {
      const mousePosition = get(findMousePositionState(mousePosKey))
      const offsets = scrollPosKeys.map((key) =>
        get(findScrollPositionState(key)),
      )

      return translateMousePosition(mousePosition, ...offsets)
    },
})

export const useSetMousePosition = <T extends HTMLElement>(
  key: string,
): MutableRefObject<T | null> => {
  const nodeRef = useRef<T | null>(null)
  const setPosition = useSetRecoilState(findMousePositionState(key))

  useEffect(() => {
    const setFromEvent = (e: Event) => {
      if (nodeRef.current) {
        const { scrollX, scrollY } = window
        const { clientX, clientY } = e as MouseEvent
        const { offsetTop, offsetLeft } = nodeRef.current || {
          offsetTop: 0,
          offsetLeft: 0,
        }
        const x = clientX - offsetLeft + scrollX
        const y = clientY - offsetTop + scrollY
        setPosition({ x, y })
      }
    }
    window.addEventListener(`mousemove`, setFromEvent)

    return () => {
      window.removeEventListener(`mousemove`, setFromEvent)
    }
  }, [setPosition])

  return nodeRef
}
